import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  storeToken,
  deleteToken,
  getDecodedToken,
  hasValidToken,
} from "../api/storage";
import {
  login as loginRequest,
  registerEmail as registerRequest,
  getProfile,
  exchangeHandoff,
} from "../api/auth";
import { registerWebDevice, deactivateWebDevice } from "../api/device";
import { attachCampaignVisitor } from "../utils/campaign";

const AuthContext = createContext(null);

// The mobile app opens the website as …/pricing?code=XYZ#h=<ticket> so the
// user arrives signed in. The ticket is taken out of the address bar straight
// away (it must not linger in history or be shared) and swapped for a session
// once — the promise is shared so a re-run of the effect can't spend it twice.
let handoffPromise = null;
const signInFromHandoff = () => {
  if (handoffPromise) return handoffPromise;
  let ticket = null;
  try {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    ticket = hash.get("h");
    if (ticket) {
      hash.delete("h");
      const rest = hash.toString();
      window.history.replaceState(
        window.history.state,
        "",
        window.location.pathname +
          window.location.search +
          (rest ? `#${rest}` : "")
      );
    }
  } catch (_) {}
  handoffPromise = (async () => {
    if (!ticket) return;
    try {
      const { token } = await exchangeHandoff(ticket);
      storeToken(token);
      // Fire-and-forget: credit any campaign link this browser came through.
      attachCampaignVisitor();
    } catch (e) {
      // Old or already-used link: carry on with whatever session exists.
      if (!hasValidToken()) {
        toast.info("انتهت صلاحية الرابط، سجّل الدخول للمتابعة");
      }
    }
  })();
  return handoffPromise;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Hydrate from any stored token on first load.
  useEffect(() => {
    const bootstrap = async () => {
      await signInFromHandoff();
      if (!hasValidToken()) {
        deleteToken();
        setLoading(false);
        return;
      }
      // A returning session (or one just handed over from the app): credit any
      // campaign link this browser came through. Fire-and-forget, once per
      // session per page load.
      attachCampaignVisitor();
      const decoded = getDecodedToken();
      // Optimistically set a minimal user from the token, then hydrate from the
      // profile endpoint (best-effort — may fail offline).
      setUser({ _id: decoded._id });
      try {
        const profile = await getProfile(decoded._id);
        setUser({ _id: decoded._id, ...profile });
        // Fire-and-forget: web counts as a device (day-throttled upsert).
        registerWebDevice(decoded._id);
      } catch (e) {
        // keep the minimal user
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, []);

  const handleAuthSuccess = useCallback((data) => {
    // data = { token, ...user }
    const { token, ...rest } = data;
    storeToken(token);
    setUser(rest);
    // Fire-and-forget: web counts as a device (day-throttled upsert).
    registerWebDevice(rest._id);
    // Fire-and-forget: credit any campaign link this browser came through
    // (email login and sign-up both land here).
    attachCampaignVisitor();
    return rest;
  }, []);

  const login = useCallback(
    async ({ email, password }) => {
      const data = await loginRequest({ email, password });
      return handleAuthSuccess(data);
    },
    [handleAuthSuccess]
  );

  const register = useCallback(
    async ({ email, password, gameName }) => {
      const data = await registerRequest({ email, password, gameName });
      return handleAuthSuccess(data);
    },
    [handleAuthSuccess]
  );

  const logout = useCallback(() => {
    // Fire-and-forget, but BEFORE deleteToken — the deactivate request needs
    // the token (it snapshots it internally, since deleteToken below runs
    // before axios's async request interceptor would read it).
    deactivateWebDevice();
    deleteToken();
    setUser(null);
    navigate("/login");
  }, [navigate]);

  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    register,
    logout,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
